/**
 * Backup: alle Daten als JSON-Datei exportieren und wieder einlesen.
 *
 * Auf dem iPad landet der Export ueber das normale Download-/Teilen-Blatt
 * von Safari, z. B. in "Dateien" oder iCloud Drive.
 */
import type {
  BackupFile,
  Board,
  DrawingDoc,
  Note,
  Project,
  Release,
  Room,
  Setlist,
  Settings,
  Song,
} from '../types.ts';
import { STORE, clear, getAll, putMany } from './idb.ts';
import { getSettings, saveSettings } from './repo.ts';

export async function buildBackup(): Promise<BackupFile> {
  const [projects, songs, setlists, notes, drawings, rooms, boards, releases, settings] =
    await Promise.all([
    getAll<Project>(STORE.projects),
    getAll<Song>(STORE.songs),
    getAll<Setlist>(STORE.setlists),
    getAll<Note>(STORE.notes),
    getAll<DrawingDoc>(STORE.drawings),
    getAll<Room>(STORE.rooms),
    getAll<Board>(STORE.boards),
    getAll<Release>(STORE.releases),
    getSettings(),
  ]);
  return {
    format: 'leadsheet-backup',
    version: 1,
    exportedAt: new Date().toISOString(),
    projects,
    songs,
    setlists,
    notes,
    drawings,
    rooms,
    boards,
    releases,
    settings,
  };
}

/** Dateiname mit Datum, z. B. leadsheets-2026-09-15.json */
export function backupFileName(date = new Date()): string {
  const iso = date.toISOString().slice(0, 10);
  return `leadsheets-${iso}.json`;
}

/** Loest den Download der Backup-Datei aus. */
export function downloadBackup(data: BackupFile): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = backupFileName(new Date(data.exportedAt));
  document.body.append(a);
  a.click();
  a.remove();
  // Etwas Luft lassen, damit Safari den Blob noch lesen kann.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export interface ImportResult {
  projects: number;
  songs: number;
  setlists: number;
  notes: number;
  drawings: number;
  rooms: number;
  boards: number;
  releases: number;
}

/** Prueft grob, ob das JSON eine Backup-Datei dieser App ist. */
export function parseBackup(text: string): BackupFile {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Die Datei ist kein gueltiges JSON.');
  }
  const file = data as Partial<BackupFile>;
  if (file?.format !== 'leadsheet-backup' || !Array.isArray(file.songs)) {
    throw new Error('Das ist keine Backup-Datei dieser App.');
  }
  return {
    format: 'leadsheet-backup',
    version: 1,
    exportedAt: file.exportedAt ?? new Date().toISOString(),
    projects: file.projects ?? [],
    songs: file.songs ?? [],
    setlists: file.setlists ?? [],
    notes: file.notes ?? [],
    drawings: file.drawings ?? [],
    // Aeltere Backups kennen die Band-Bibel noch nicht.
    rooms: file.rooms ?? [],
    boards: file.boards ?? [],
    releases: file.releases ?? [],
    settings: file.settings ?? null,
  };
}

/**
 * Spielt ein Backup ein.
 * mode "replace" loescht vorher alles, "merge" ueberschreibt nur
 * Datensaetze mit gleicher ID.
 */
export async function restoreBackup(
  file: BackupFile,
  mode: 'merge' | 'replace',
): Promise<ImportResult> {
  if (mode === 'replace') {
    await clear([
      STORE.projects,
      STORE.songs,
      STORE.setlists,
      STORE.notes,
      STORE.drawings,
      STORE.rooms,
      STORE.boards,
      STORE.releases,
    ]);
  }
  await putMany<Project>(STORE.projects, file.projects);
  await putMany<Song>(STORE.songs, file.songs);
  await putMany<Setlist>(STORE.setlists, file.setlists);
  await putMany<Note>(STORE.notes, file.notes);
  await putMany<DrawingDoc>(STORE.drawings, file.drawings);
  await putMany<Room>(STORE.rooms, file.rooms ?? []);
  await putMany<Board>(STORE.boards, file.boards ?? []);
  await putMany<Release>(STORE.releases, file.releases ?? []);
  if (file.settings) {
    const current = await getSettings();
    // Einstellungen zusammenfuehren, aber die id nicht ueberschreiben.
    await saveSettings({ ...current, ...(file.settings as Settings), id: 'app' });
  }
  return {
    projects: file.projects.length,
    songs: file.songs.length,
    setlists: file.setlists.length,
    notes: file.notes.length,
    drawings: file.drawings.length,
    rooms: (file.rooms ?? []).length,
    boards: (file.boards ?? []).length,
    releases: (file.releases ?? []).length,
  };
}

/** Oeffnet den Dateiauswahl-Dialog und liefert den Textinhalt. */
export function pickJsonFile(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.remove();
      if (!file) {
        resolve(null);
        return;
      }
      void file.text().then(resolve);
    });
    document.body.append(input);
    input.click();
  });
}
